import { email, store } from '@/firebase'
import {
  CollectionName,
  FSTeam,
  FSTeamAuthorizedService,
  FSTeamMember,
  FSUserPublicProfile,
  NitteServiceType
} from '@WombatTechnology/nitte-time-lib'
import {
  CollectionReference,
  DocumentReference,
  collection,
  doc,
  getDocs,
  setDoc,
  updateDoc
} from 'firebase/firestore'
import { TeamPlan } from './teamPlan'
import { TeamAuthorizedService } from './teams/teamAuthorizedService'
import { TeamMember } from './teams/teamMember'
import { toCollectionData, toData } from './toData'

export class Team {
  constructor(
    readonly ref: DocumentReference<FSTeam>,
    readonly data: FSTeam,
    readonly members: TeamMember[],
    readonly plan: TeamPlan,
    readonly authorizedServices: FSTeamAuthorizedService[]
  ) {}

  get id(): string {
    return this.ref.id
  }
  get name(): string {
    return this.data.name
  }

  get currentMember() {
    return this.members.find((m) => m.email === email()!)
  }

  get approvedMembers() {
    return this.members.filter((m) => m.approved)
  }

  get invitingMembers() {
    return this.members.filter((m) => !m.approved)
  }

  get price() {
    return this.plan.pricePerUser * this.approvedMembers.length
  }

  get useMediatory() {
    return this.plan.data.useMediatory
  }

  isAuthorized(service: TeamAuthorizedService) {
    return this.authorizedServices.some((s) => s.service === service)
  }

  async updateName(name: string) {
    return updateDoc(this.ref, { name })
  }

  async invite(emails: string[], service: NitteServiceType) {
    return Promise.all(
      emails.map((email) =>
        setDoc(TeamMember.doc(this.id, email), {
          email,
          approved: false,
          isAdmin: false,
          isPaymentAdmin: false,
          userPublicProfileRef: null,
          invitedFrom: service
        } as FSTeamMember<DocumentReference<FSUserPublicProfile>>)
      )
    )
  }

  findMemberByUid(uid: string) {
    return this.members.find((m) => m.uid === uid)
  }

  static async build(id: string) {
    const ref = Team.doc(id)
    const data = await toData(ref)
    const membersCollection = await toCollectionData(TeamMember.collection(id))
    const teamPlan = await TeamPlan.build(id)
    const members = await Promise.all(
      membersCollection.map((m) =>
        TeamMember.build(id, m.email, teamPlan.paymendAdminUid)
      )
    )
    const authorizedServices = await toCollectionData(
      TeamAuthorizedService.collection(id)
    )
    return new Team(ref, data, members, teamPlan, authorizedServices)
  }

  /**
   * ユーザーの所属しているチームの一覧を取得する
   * @returns
   */
  static async userBelongingsTeamDocs() {
    const membersOfUserQuery = TeamMember.membersQuery(email()!, true)
    const qs = await getDocs(membersOfUserQuery)
    return qs.docs.map((d) => Team.docFromMember(d.ref))
  }

  static doc(id: string) {
    return doc<FSTeam, FSTeam>(this.collection(), id)
  }

  static docFromMember(memberDoc: DocumentReference) {
    return memberDoc.parent.parent as DocumentReference<FSTeam>
  }

  static collection() {
    return collection(store, `${CollectionName.Teams}`) as CollectionReference<
      FSTeam,
      FSTeam
    >
  }
}
